import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Info } from 'react-feather';
import styled from '@emotion/styled';
import { Box } from '@mui/material';
import { ChatContextType, type ChatContextItem } from '@trinity/chat';
import {
  LinkContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { PageRenderer } from '@trinity/chat-components/components/PageDashboard/PageRenderer/index.js';
import AudioPlayerController from '@trinity/chat-components/components/TextToSpeechAudioPlayer/AudioPlayerController.js';
import { Constants } from '@trinity/chat-components/components/Theme';
import type { CodeWidgetHostActions } from '@trinity/chat-components/components/Widgets/CodeWidgetHostActionsContext';
import { CodeWidgetHostActionsContext } from '@trinity/chat-components/components/Widgets/CodeWidgetHostActionsContext';
import { RepDashboardProfileClickContext } from '@trinity/chat-components/components/Widgets/RepDashboardProfileClickContext';
import { RepDashboardSubmitQueryContext } from '@trinity/chat-components/components/Widgets/RepDashboardSubmitQueryContext';
import { BrandingProvider } from '@trinity/chat-components/containers/Branding';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { AudioControls } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/AudioControls';
import { TextToSpeechStateContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useModal } from '@trinity/chat-components/hooks/useModal';
import {
  ActivePageContext,
  MessagesListRefContext,
  ChatHasAccessContext,
  ChatSessionItemsContext,
  AuthorContext,
  ChatLandingCardsVisibleContext,
  ActiveChatSessionContext,
} from './context';
import { ChatGuidelinesModal } from './GuidelinesModal';
import { useChatGuidelinesCookie } from './hooks/useChatGuidelinesCookie';
import { useChatPermissions } from './hooks/useChatPermissions';
import { useCustomPageActionDispatch } from './hooks/useCustomPageActionDispatch';
import { UnsubmittedMessage } from './Input';
import { LandingCards } from './LandingCards';
import { messageLaneWidth } from './Message.styles';
import { InitialSystemMessage } from './Message.System.Initial';
import { MessageList } from './MessageList';
import { NoAccessBanner } from './NoAccessBanner';

type Props = {
  className?: string;
  isSidePanel?: boolean;
};

type AgentContextDataWithMode = Pick<ChatContextItem, 'subject'> & {
  mode?: string;
};

function computeChatVisibility(isSidePanel: boolean | undefined, isSidePanelExpanded: boolean, activePageId: number | null, chatPermissions: { canModify: boolean }, disableChatInputWithLandingCards: boolean | undefined, showLandingCards: boolean, pageWithSideChat: boolean) {
  if (pageWithSideChat) {
    return {
      showPageArea: true,
      showMessages: true,
      showInput: chatPermissions.canModify && !(disableChatInputWithLandingCards && showLandingCards),
    };
  }
  const showPageArea = (!isSidePanel && isSidePanelExpanded) || activePageId != null;
  const showMessages = (isSidePanel && isSidePanelExpanded) || activePageId == null;
  const showInput = !(!isSidePanel && isSidePanelExpanded) &&
    chatPermissions.canModify &&
    !(disableChatInputWithLandingCards && showLandingCards);
  return { showPageArea, showMessages, showInput };
}

const SIDE_CHAT_DEFAULT_WIDTH = 425;

function useSideChatResize() {
  const [width, setWidth] = useState(SIDE_CHAT_DEFAULT_WIDTH);
  const rootRef = useRef<HTMLDivElement>(null);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const parentWidth = rootRef.current?.clientWidth ?? 0;
    const maxWidth = Math.max(SIDE_CHAT_DEFAULT_WIDTH, Math.floor(parentWidth * 0.5));

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      setWidth(Math.min(maxWidth, Math.max(SIDE_CHAT_DEFAULT_WIDTH, startWidth + delta)));
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width]);

  return { width, rootRef, handleResizeMouseDown };
}

function ChatPageArea({ isSidePanel, fillWidth, codeWidgetHostActions, handleRepProfileClick, handleRepSubmitQuery, activePageId, proxyFetch, brandIdentifier, apiClient, activeAgentContextData, dispatchCustomPageAction }: {
  isSidePanel?: boolean;
  fillWidth?: boolean;
  codeWidgetHostActions: CodeWidgetHostActions;
  handleRepProfileClick: (profileId: string, name: string) => void;
  handleRepSubmitQuery: (query: string) => void;
  activePageId: number | null;
  proxyFetch: any;
  brandIdentifier: string | null;
  apiClient: any;
  activeAgentContextData: AgentContextDataWithMode | null;
  dispatchCustomPageAction: any;
}) {
  let PageRendererRoot = FullPageRenderer;
  if (isSidePanel) PageRendererRoot = SidePanelPageRenderer;
  else if (fillWidth) PageRendererRoot = FillPageRenderer;
  return (
    <PageArea>
      <CodeWidgetHostActionsContext.Provider value={codeWidgetHostActions}>
        <RepDashboardProfileClickContext.Provider value={handleRepProfileClick}>
          <RepDashboardSubmitQueryContext.Provider value={handleRepSubmitQuery}>
            <PageRendererRoot>
              <PageRenderer
                pageId={activePageId}
                proxyFetch={proxyFetch}
                brandIdentifier={brandIdentifier ?? undefined}
                chatAuthenticatedClient={apiClient.authenticatedClient}
                chatContextSubject={activeAgentContextData?.subject ?? null}
                chatContextMode={activeAgentContextData?.mode ?? null}
                dispatchCustomPageAction={dispatchCustomPageAction}
              />
            </PageRendererRoot>
          </RepDashboardSubmitQueryContext.Provider>
        </RepDashboardProfileClickContext.Provider>
      </CodeWidgetHostActionsContext.Provider>
    </PageArea>
  );
}

function ChatFooter({ renderAudioPlayer, showInput, showChatGuidelines, chatGuidelines, toggleGuidelinesModal }: {
  renderAudioPlayer: () => React.ReactNode;
  showInput: boolean;
  showChatGuidelines: boolean;
  chatGuidelines: string | undefined;
  toggleGuidelinesModal: () => void;
}) {
  return (
    <Footer>
      {renderAudioPlayer()}
      {showInput && <UnsubmittedMessage />}
      {showChatGuidelines && (
        <DisclaimerContainer>
          <DisclaimerCopy>
            Shared content is meant for internal use only and not meant to be shared externally.
          </DisclaimerCopy>
          <OpenDisclaimer onClick={toggleGuidelinesModal}>
            View Usage Guidelines <Info size={16} />
          </OpenDisclaimer>
        </DisclaimerContainer>
      )}
    </Footer>
  );
}

export const ChatInstance = (props: Props) => {
  const hasAccess = useContext(ChatHasAccessContext);
  const link = useContext(LinkContext);
  const chatPermissions = useChatPermissions();

  const [, setMessagesRef] = useContext(MessagesListRefContext);
  const author = useContext(AuthorContext);
  const isFirstSession = author.isFirstSession;
  const { isDismissed: chatGuidelinesDismissed } = useChatGuidelinesCookie();
  const [toggleGuidelinesModal, GuidelinesModal] = useModal(ChatGuidelinesModal, {
    defaultOpen: isFirstSession && !chatGuidelinesDismissed,
  });
  const features = useContext(GroupFeaturesContext);
  const chatGuidelines = features?.chatGuidelines;
  const groupId = author?.group?.id;
  const showChatGuidelines = !!chatGuidelines && groupId !== 3;
  const disableChatInputWithLandingCards = features?.disableChatInputWithLandingCards;

  const items = useContext(ChatSessionItemsContext);

  const showLandingCards = useContext(ChatLandingCardsVisibleContext);

  const { pageId: activePageId, layout: activeLayout, clearPage } = useContext(ActivePageContext);
  const apiClient = useChatApiClient();
  const chat = useContext(ActiveChatSessionContext);
  const dispatchCustomPageAction = useCustomPageActionDispatch();
  const activeAgentContextData: AgentContextDataWithMode | null =
    chat.context.type === ChatContextType.Agent ? (chat.context.data) : null;
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const proxyFetch = useCallback(
    ({ agentId, extensionPath }: { agentId: number; extensionPath: string }) =>
      apiClient.pages.proxyWidgetData(agentId, extensionPath, brandIdentifier ?? undefined),
    [apiClient, brandIdentifier]
  );
  const [isSidePanelExpanded] = useContext(SidePanelExpandedContext);

  const handleRepProfileClick = useCallback(
    (profileId: string, name: string) => {
      clearPage();
      if (chat.context.type !== ChatContextType.Agent || !brandIdentifier) return;

      dispatch({
        type: 'new-chat-started',
        payload: {
          brandIdentifier,
          context: {
            ...chat.context,
            data: {
              ...chat.context.data,
              subject: {
                id: profileId,
                name,
              },
            },
          },
          pendingQuery: `Show me more about ${name}`,
        },
      });
    },
    [clearPage, chat.context, brandIdentifier, dispatch]
  );

  const handleRepSubmitQuery = useCallback(
    (query: string) => {
      clearPage();
      if (chat.context.type !== ChatContextType.Agent || !brandIdentifier) return;

      dispatch({
        type: 'new-chat-started',
        payload: {
          brandIdentifier,
          context: chat.context,
          pendingQuery: query,
        },
      });
    },
    [clearPage, chat.context, brandIdentifier, dispatch]
  );

  const renderMessageList = useCallback(() => {
    if (!link.initialized) return null;
    if (showLandingCards) return <LandingCards />;
    if (!items.length) return <InitialSystemMessage />;
    return <MessageList items={items} />;
  }, [items, link.initialized, showLandingCards]);

  const showAudioPlayer = useShowAudioPlayer();

  const renderAudioPlayer = useCallback(() => {
    if (!showAudioPlayer) return null;

    return (
      <AudioPlayerRow>
        <AudioPlayerBox>
          <AudioPlayerController>
            {({
              duration,
              position,
              isLoading,
              onScan,
              onSeek,
              onStop,
              onPreviousTrack,
              onNextTrack,
              onSkipBackward,
              onSkipAhead,
              playing,
              onTogglePlay,
              canSkipBackward,
              canSkipForward,
            }) => (
              <AudioControls
                isPlaying={playing}
                isLoading={isLoading}
                onTogglePlay={onTogglePlay}
                onPreviousTrack={onPreviousTrack}
                onNextTrack={onNextTrack}
                onSkipBackward={onSkipBackward}
                onSkipAhead={onSkipAhead}
                disabledPreviousTrack={!canSkipBackward}
                disabledNextTrack={!canSkipForward}
                onStop={onStop}
                currentTime={position}
                duration={duration}
                onScan={onScan}
                onSeek={onSeek}
              />
            )}
          </AudioPlayerController>
        </AudioPlayerBox>
      </AudioPlayerRow>
    );
  }, [showAudioPlayer]);

  const codeWidgetHostActions = useMemo<CodeWidgetHostActions>(
    () => ({ submitQuery: handleRepSubmitQuery, viewProfile: handleRepProfileClick }),
    [handleRepSubmitQuery, handleRepProfileClick]
  );

  const pageWithSideChat = activePageId != null && activeLayout === 'side_chat' && !props.isSidePanel;

  const { width: sideChatWidth, rootRef: sideChatRef, handleResizeMouseDown } = useSideChatResize();

  const { showPageArea, showMessages, showInput } = computeChatVisibility(
    props.isSidePanel, isSidePanelExpanded, activePageId, chatPermissions, disableChatInputWithLandingCards, showLandingCards, pageWithSideChat
  );

  const pageArea = showPageArea && (
    <ChatPageArea
      isSidePanel={props.isSidePanel}
      fillWidth={pageWithSideChat}
      codeWidgetHostActions={codeWidgetHostActions}
      handleRepProfileClick={handleRepProfileClick}
      handleRepSubmitQuery={handleRepSubmitQuery}
      activePageId={activePageId}
      proxyFetch={proxyFetch}
      brandIdentifier={brandIdentifier}
      apiClient={apiClient}
      activeAgentContextData={activeAgentContextData}
      dispatchCustomPageAction={dispatchCustomPageAction}
    />
  );

  const messagesArea = showMessages && (
    <Messages>
      <MessagesWrap ref={setMessagesRef}>{renderMessageList()}</MessagesWrap>
    </Messages>
  );

  const footer = (
    <ChatFooter
      renderAudioPlayer={renderAudioPlayer}
      showInput={showInput}
      showChatGuidelines={showChatGuidelines}
      chatGuidelines={chatGuidelines ?? undefined}
      toggleGuidelinesModal={toggleGuidelinesModal}
    />
  );

  const body = renderChatBody({
    pageWithSideChat,
    sideChatRef,
    sideChatWidth,
    handleResizeMouseDown,
    pageArea,
    messagesArea,
    footer,
  });

  return (
    <BrandingProvider>
      <ChatInstanceRoot className={props.className}>
        {!hasAccess && <NoAccessBanner />}
        {body}
        {showChatGuidelines && <GuidelinesModal guidelines={chatGuidelines} />}
      </ChatInstanceRoot>
    </BrandingProvider>
  );
};

function renderChatBody({
  pageWithSideChat,
  sideChatRef,
  sideChatWidth,
  handleResizeMouseDown,
  pageArea,
  messagesArea,
  footer,
}: {
  pageWithSideChat: boolean;
  sideChatRef: React.RefObject<HTMLDivElement | null>;
  sideChatWidth: number;
  handleResizeMouseDown: (e: React.MouseEvent) => void;
  pageArea: React.ReactNode;
  messagesArea: React.ReactNode;
  footer: React.ReactNode;
}) {
  if (pageWithSideChat) {
    return (
      <SideChatRoot ref={sideChatRef}>
        <SideChatLeftPane>{pageArea}</SideChatLeftPane>
        <SideChatResizeHandle onMouseDown={handleResizeMouseDown} />
        <SideChatRightPane $width={sideChatWidth}>
          {messagesArea}
          {footer}
        </SideChatRightPane>
      </SideChatRoot>
    );
  }
  return (
    <>
      {pageArea}
      {messagesArea}
      {footer}
    </>
  );
}

function useShowAudioPlayer() {
  const features = useContext(GroupFeaturesContext);
  const audioPlayerState = useContext(TextToSpeechStateContext);
  const audioPlayerEnabled = features?.chatReadAloud && features?.chatVoiceSource !== 'browser';

  return useMemo(
    () => audioPlayerEnabled && (!!audioPlayerState.queryId || !!audioPlayerState.kolSearchId),
    [audioPlayerEnabled, audioPlayerState]
  );
}

const MaxContainerWidth = 1100;

export const DisclaimerContainer = styled.div({
  width: messageLaneWidth,
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  containerType: 'inline-size',
  fontSize: 14,
});

const DisclaimerCopy = styled.span({
  textAlign: 'center',
  '@container (max-width: 640px)': {
    display: 'none',
  },
});

const OpenDisclaimer = styled.div(({ theme }) => ({
  display: 'inline-flex',
  color: theme.palette.sentiment.primary.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  marginLeft: 10,
  alignItems: 'center',
  gap: 4,
  '@container (max-width: 640px)': {
    marginLeft: 0,
    marginTop: 4,
  },
}));

export const ChatInstanceRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: `calc(100% - ${Constants.Modal.HeaderHeight}px)`,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
});

const Messages = styled.div({
  boxSizing: 'border-box',
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 40px, 100% 40px, 100% 14px, 100% 14px',
  backgroundAttachment: 'local, local, scroll, scroll',
});

const PageArea = styled.div({
  boxSizing: 'border-box',
  flex: 1,
  minHeight: 0,
  height: '100%',
  overflow: 'hidden',
});

export const MessagesWrap = styled.div({
  minHeight: '100%',
  width: '100%',
  maxWidth: MaxContainerWidth,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
  paddingTop: 15,
  paddingRight: 10,
  paddingLeft: 10,
  boxSizing: 'border-box',
});

export const Footer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: `20px 10px ${15}px 10px`,
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: MaxContainerWidth,
  margin: '0 auto',
});

const AudioPlayerRow = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'absolute',
  right: 9,
  bottom: `calc(100% - 8px)`,
});

const AudioPlayerBox = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: theme.palette.white.main,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  padding: 10,
  width: 'auto',
  '&:hover': {
    border: `2px solid ${theme.palette.primary.main}`,
  },
}));

const FullPageRenderer = styled(Box)(({ theme }) => ({
  width: '85%',
  maxWidth: '85%',
  height: 'calc(100% - 24px)',
  margin: '0 auto',
  marginTop: '24px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const SidePanelPageRenderer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.gray.light2}`,
  borderTop: `2px solid ${theme.palette.gray.light2}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const FillPageRenderer = styled(Box)({
  width: '100%',
  maxWidth: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const SideChatRoot = styled.div({
  flex: 1,
  display: 'flex',
  minHeight: 0,
  width: '100%',
  overflow: 'hidden',
});

const SideChatLeftPane = styled.div({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const SideChatRightPane = styled.div<{ $width: number }>(({ $width }) => ({
  width: $width,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const SideChatResizeHandle = styled.div({
  width: 8,
  flexShrink: 0,
  cursor: 'col-resize',
  background: 'transparent',
  '&::after': {
    content: '""',
    display: 'block',
    width: 1,
    height: '100%',
    margin: '0 auto',
    background: 'rgba(0, 0, 0, 0.08)',
    transition: 'background 0.15s',
  },
  '&:hover::after, &:active::after': {
    background: 'rgba(86, 90, 221, 0.5)',
    width: 3,
  },
});

export default ChatInstance;
